import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUI } from '@/store/ui'
import { useEntityPeek } from '@/components/EntityPeek'
import { AgentAPI } from '@/api/agent'
import { ApiError } from '@/api/client'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { renderAnswer } from '@/lib/citations'
import './familiar-chat.css'

// ✦ Familiar — a real agent over your constellation. It answers in the graph's own notation:
// entities come back as [[Exact Name]], which resolve to nodes you can click into the Constellation.
//
// Anatomy is a real chat column: a scrollable transcript fills the rail (newest at the
// bottom), and the composer — suggestion chips, scope, pins, input — is pinned underneath.

interface Msg {
  role: 'user' | 'agent'
  text: string
  at: number
  failed?: boolean
}

/** Three questions worth asking. The first names the busiest node, so it adapts to the graph. */
function openings(nodes: GraphNode[], edgeCount: Map<string, number>): string[] {
  const hub = [...nodes]
    .filter((n) => !n.self)
    .sort((a, b) => (edgeCount.get(b.id) ?? 0) - (edgeCount.get(a.id) ?? 0))[0]
  return [
    'What am I looking at?',
    hub ? `Who connects me to ${hub.name}?` : 'Who are the people I know best?',
    'Which relationships have gone cold?',
  ]
}

function clock(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export function Familiar() {
  const { diwaUrl } = useUI()
  const { openPeek } = useEntityPeek()
  const agent = useMemo(() => new AgentAPI(diwaUrl), [diwaUrl])
  const graph = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])

  const [messages, setMessages] = useState<Msg[]>([])
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [muted, setMuted] = useState<Set<string>>(new Set())
  // @-pins: entities guaranteed into the familiar's context, the way an editor pins files.
  const [pins, setPins] = useState<GraphNode[]>([])
  const [mention, setMention] = useState<string | null>(null) // text after an open "@", null = closed
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data: status } = useQuery({ queryKey: ['agent-status'], queryFn: () => agent.status() })
  // Same key GraphView uses — on the Constellation this is already cached, so no second fetch.
  const { data: network } = useQuery({ queryKey: ['graph-network', 'all'], queryFn: () => graph.network('all') })

  const byName = useMemo(() => {
    const m = new Map<string, GraphNode>()
    for (const n of network?.nodes ?? []) m.set(n.name.toLowerCase(), n)
    return m
  }, [network])

  // Domains present in the graph. Muting is the exception, so a domain you add later is included
  // by default rather than silently invisible to the familiar.
  const allDomains = useMemo(
    () => [...new Set((network?.nodes ?? []).filter((n) => !n.self).map((n) => n.domain))].sort(),
    [network],
  )
  const active = useMemo(() => allDomains.filter((d) => !muted.has(d)), [allDomains, muted])

  const suggestions = useMemo(() => {
    const degree = new Map<string, number>()
    for (const e of network?.edges ?? []) {
      degree.set(e.source, (degree.get(e.source) ?? 0) + 1)
      degree.set(e.target, (degree.get(e.target) ?? 0) + 1)
    }
    return openings(network?.nodes ?? [], degree)
  }, [network])

  // Auto-scroll the transcript itself (not the whole rail) to the newest message.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages, busy])

  // Matches for the open @-mention, best-connected first so the likely target is on top.
  const mentionMatches = useMemo(() => {
    if (mention === null) return []
    const needle = mention.toLowerCase()
    const degree = new Map<string, number>()
    for (const e of network?.edges ?? []) {
      degree.set(e.source, (degree.get(e.source) ?? 0) + 1)
      degree.set(e.target, (degree.get(e.target) ?? 0) + 1)
    }
    return (network?.nodes ?? [])
      .filter((n) => !n.self && n.name.toLowerCase().includes(needle))
      .filter((n) => !pins.some((p) => p.id === n.id))
      .sort((a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0))
      .slice(0, 6)
  }, [mention, network, pins])

  /** An "@" is open when it sits before the caret with no later whitespace break. */
  function syncMention(value: string, caret: number) {
    const upToCaret = value.slice(0, caret)
    const at = upToCaret.lastIndexOf('@')
    if (at === -1) return setMention(null)
    const after = upToCaret.slice(at + 1)
    // Allow spaces inside a mention ("@Founder Inst…") but close once a name was clearly finished.
    setMention(/[@\n]/.test(after) ? null : after)
  }

  function pin(node: GraphNode) {
    setPins((prev) => (prev.some((p) => p.id === node.id) ? prev : [...prev, node]))
    // Replace the open "@query" with the entity's name so the question reads naturally.
    setQ((prev) => {
      const at = prev.lastIndexOf('@')
      return at === -1 ? prev : `${prev.slice(0, at)}${node.name} `
    })
    setMention(null)
    inputRef.current?.focus()
  }

  function unpin(id: string) {
    setPins((prev) => prev.filter((p) => p.id !== id))
  }

  function toggleDomain(d: string) {
    setMuted((prev) => {
      const next = new Set(prev)
      if (next.has(d)) next.delete(d)
      else if (active.length > 1) next.add(d) // never mute the last domain — an empty graph answers nothing
      return next
    })
  }

  async function send(text: string) {
    if (!text.trim() || busy) return
    setMessages((m) => [...m, { role: 'user', text, at: Date.now() }])
    setQ('')
    setBusy(true)
    try {
      // Omit `domains` when nothing is muted, so the backend keeps its own default.
      const r = await agent.ask(text, {
        domains: muted.size ? active : undefined,
        focus: pins.map((p) => p.id),
        surface: window.location.pathname, // she knows where the keeper stands
      })
      setMessages((m) => [...m, { role: 'agent', text: r.answer, at: Date.now() }])
    } catch (err) {
      const reason =
        err instanceof ApiError && err.status === 502
          ? "The familiar couldn't reach Claude. Try again."
          : 'The familiar stopped short. Try again.'
      setMessages((m) => [...m, { role: 'agent', text: reason, at: Date.now(), failed: true }])
    } finally {
      setBusy(false)
    }
  }

  // A cited entity opens the peek anchored to the clicked citation — a quick look with an
  // explicit route into the Constellation, rather than a silent select that never navigates.
  function pick(node: GraphNode, anchor: HTMLElement) {
    openPeek(node, anchor)
  }

  // A configured key is the difference between an agent and an empty box. Say which one this is.
  if (status && !status.key_configured) {
    return (
      <div>
        <div className="dw-rail-section-title">✦ Tala</div>
        <div className="dw-notice">
          The familiar has no model key. Add <code>DIWA_ANTHROPIC_API_KEY</code> to the backend
          environment and restart it.
        </div>
      </div>
    )
  }

  return (
    <div className="dw-fam">
      <div className="dw-rail-section-title dw-fam-head">✦ Tala</div>

      {/* Transcript — fills the rail, scrolls independently, newest at the bottom. */}
      <div className="dw-fam-scroll" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="dw-rail-empty dw-fam-empty">Ask about your constellation.</div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`dw-fam-turn dw-fam-turn--${m.role}`}>
            <div
              className={[
                'dw-msg',
                m.role === 'user' ? 'dw-msg--user' : 'dw-msg--agent',
                m.failed ? 'dw-msg--failed' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {m.role === 'agent' && <span className="dw-fam-mark" aria-hidden="true">✦</span>}
              {m.role === 'agent' && !m.failed ? renderAnswer(m.text, byName, pick) : m.text}
            </div>
            <div className="dw-fam-time">{clock(m.at)}</div>
          </div>
        ))}

        {busy && (
          <div className="dw-fam-turn dw-fam-turn--agent">
            <div className="dw-msg dw-msg--agent dw-thinking">
              <span className="dw-thinking-star">✦</span> Reading the graph
            </div>
          </div>
        )}
      </div>

      {/* Composer — pinned to the bottom of the rail, always visible. */}
      <div className="dw-fam-composer">
        {messages.length === 0 && suggestions.length > 0 && (
          <div className="dw-chip-row dw-fam-suggest-row">
            {suggestions.map((s) => (
              <button key={s} type="button" className="dw-fam-suggest" onClick={() => send(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        {allDomains.length > 1 && (
          <div className="dw-fam-scope">
            <div className="dw-rail-section-title" style={{ marginBottom: 0 }}>Reading</div>
            <div className="dw-chip-row">
              {allDomains.map((d) => {
                const on = !muted.has(d)
                return (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={on}
                    className={`dw-chip dw-chip--toggle${on ? ' dw-chip--on' : ''}`}
                    onClick={() => toggleDomain(d)}
                  >
                    {d}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {pins.length > 0 && (
          <div className="dw-chip-row">
            {pins.map((p) => (
              <button
                key={p.id}
                type="button"
                className="dw-chip dw-chip--pin"
                title="Unpin — stop guaranteeing this entity into the familiar's context"
                onClick={() => unpin(p.id)}
              >
                ✧ {p.name} ×
              </button>
            ))}
          </div>
        )}

        <div className="dw-fam-inputrow">
          {mention !== null && mentionMatches.length > 0 && (
            <div className="dw-mention-pop" role="listbox">
              {mentionMatches.map((n, i) => (
                <button
                  key={n.id}
                  type="button"
                  role="option"
                  aria-selected={i === 0}
                  className={`dw-mention-item${i === 0 ? ' dw-mention-item--top' : ''}`}
                  onClick={() => pin(n)}
                >
                  <span className="dw-mention-name">{n.name}</span>
                  <span className="dw-mention-meta">
                    {n.type} · {n.domain}
                  </span>
                </button>
              ))}
            </div>
          )}
          <input
            ref={inputRef}
            className="dw-input"
            placeholder="ask, or @ to pin an entity"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              syncMention(e.target.value, e.target.selectionStart ?? e.target.value.length)
            }}
            onKeyDown={(e) => {
              if (mention !== null && mentionMatches.length > 0) {
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault()
                  pin(mentionMatches[0])
                  return
                }
                if (e.key === 'Escape') {
                  setMention(null)
                  return
                }
              }
              if (e.key === 'Enter') send(q)
            }}
          />
          <button
            className="dw-btn dw-fam-send"
            disabled={busy}
            aria-label="Send"
            onClick={() => send(q)}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  )
}
